import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrandedImages, useCompanyBranding, useBrandedColors } from '../hooks/useBranding';
import { vendorRegistrationService } from '../services/vendorRegistration.service';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import localeConfig from '../config/locale.config';

const Register = () => {
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState('');
    const [showOtp, setShowOtp] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [showOtpField, setShowOtpField] = useState(false);
    
    const navigate = useNavigate();
    
    // Get branded content
    const { loginLogo } = useBrandedImages();
    const { name, tagline } = useCompanyBranding();
    const colors = useBrandedColors();

    const handleRequestOTP = async () => {
        if (!mobile || mobile.length < localeConfig.mobile.minDigits) {
            setError('Please enter a valid mobile number');
            return;
        }

        setError('');
        setOtpLoading(true);

        try {
            await vendorRegistrationService.newVendorLogin(mobile);
            setOtpSent(true);
            setShowOtpField(true);
            setError('');
        } catch (err) {
            console.error('OTP request error:', err);
            setError(err.message || 'Failed to send OTP. Please try again.');
        } finally {
            setOtpLoading(false);
        }
    };

    const handleRegister = async () => {
        if (!mobile || !otp) {
            setError('Please enter mobile number and OTP');
            return;
        }

        if (otp.length !== 6) {
            setError('Please enter a valid 6-digit OTP');
            return;
        }

        setError('');
        setLoading(true);

        try {
            // First verify OTP
            const verifyResult = await vendorRegistrationService.newVendorVerifyOTP(mobile, otp);
            
            // Check if there's an existing registration
            if (verifyResult.has_existing_registration && verifyResult.registration_id) {
                // Use existing registration ID
                localStorage.setItem('current_registration_id', verifyResult.registration_id.toString());
                localStorage.setItem('vendor_mobile_number', mobile); // Store mobile number
                console.log('Using existing registration ID:', verifyResult.registration_id);
                
                // Navigate to vendor registration page to edit existing data
                navigate('/vendor-registration');
                return;
            }
            
            // No existing registration, create a new one
            const registrationData = {
                user_mobile_number: mobile,
                registration_data: {
                    vendor: {
                        vendor_name: '',
                        address: {
                            address: '',
                            state: '',
                            city: '',
                            latitude: null,
                            longitude: null,
                            pincode: ''
                        },
                        contact_number: mobile, // Use the login mobile number
                        owner_username: '',
                        owner_name: '',
                        owner_mobile: mobile, // Use the login mobile number
                        preferred_language: 'en'
                    }
                }
            };

            const registrationResult = await vendorRegistrationService.createNewVendorRegistration(registrationData);
            
            // Store registration ID and mobile number in localStorage for the vendor registration process
            if (registrationResult.registration_id) {
                localStorage.setItem('current_registration_id', registrationResult.registration_id.toString());
                localStorage.setItem('vendor_mobile_number', mobile); // Store mobile number
            }
            
            // Navigate to vendor registration page
            navigate('/vendor-registration');
        } catch (err) {
            console.error('Registration error:', err);
            
            let errorMessage = 'Registration failed. Please try again.';
            
            if (err.status === 401) {
                errorMessage = 'Invalid OTP. Please check and try again.';
            } else if (err.status === 400) {
                errorMessage = err.message || 'Invalid registration data. Please check your input.';
            } else if (err.status === 409) {
                errorMessage = 'Mobile number already exists. Please use login instead.';
            } else if (err.status === 500) {
                errorMessage = 'Server error. Please try again later.';
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    const formatMobileNumber = (value) => {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '');
        const limitedDigits = digits.slice(0, localeConfig.mobile.maxDigits);
        return limitedDigits;
    };

    const handleMobileChange = (e) => {
        const formatted = formatMobileNumber(e.target.value);
        setMobile(formatted);
        // Reset OTP state when mobile number changes
        if (otpSent) {
            setOtpSent(false);
            setShowOtpField(false);
            setOtp('');
        }
        if (error) setError('');
    };

    const handleOtpChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setOtp(value);
        if (error) setError('');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (!otpSent) {
                handleRequestOTP();
            } else {
                handleRegister();
            }
        }
    };

    return (
        <div 
            className="min-h-screen flex items-center justify-center p-4"
            style={{
                backgroundColor: colors.secondary
            }}
        >
            <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-8 w-full max-w-md">
                <div className="text-center mb-6 sm:mb-8">
                    <div className="inline-flex items-center justify-center rounded-full mb-4">
                        {loginLogo && (
                            <img 
                                src={loginLogo} 
                                alt="Company Logo" 
                            />
                        )}
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{name}</h1>
                    <p className="text-gray-600 mt-2">{tagline}</p> 
                </div>

                <div className="space-y-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-1x1 font-normal text-black mb-1">
                            Mobile Number *
                        </label>
                        <input
                            type="tel"
                            value={mobile}
                            onChange={handleMobileChange}
                            onKeyPress={handleKeyPress}
                            className="outline-none w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                            style={{
                                '--tw-ring-color': colors.primary
                            }}
                            placeholder={localeConfig.mobile.placeholder}
                            disabled={loading || otpLoading}
                        />
                        {mobile && mobile.length < localeConfig.mobile.minDigits && (
                            <p className="mt-1 text-xs text-red-600">
                                {localeConfig.mobile.errorText}
                            </p>
                        )}
                    </div>

                    {/* OTP Field */}
                    {showOtpField && (
                        <div>
                            <label className="block text-1x1 font-normal text-black mb-1">
                                Enter OTP
                            </label>
                            <div className="relative">
                                <input
                                    type={showOtp ? "text" : "password"}
                                    value={otp}
                                    onChange={handleOtpChange}
                                    onKeyPress={handleKeyPress}
                                    className="outline-none w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent pr-10"
                                    style={{
                                        '--tw-ring-color': colors.primary
                                    }}
                                    placeholder="Enter 6-digit OTP"
                                    disabled={loading}
                                    maxLength={6}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowOtp(!showOtp)}
                                >
                                    {showOtp ? (
                                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    )}
                                </button>
                            </div>
                            {otp && otp.length < 6 && (
                                <p className="mt-1 text-xs text-red-600">
                                    Please enter a 6-digit OTP
                                </p>
                            )}
                        </div>
                    )}

                    {/* Request OTP Button */}
                    {!otpSent && (
                        <button
                            onClick={handleRequestOTP}
                            disabled={!mobile || mobile.length < localeConfig.mobile.minDigits || otpLoading}
                            className="w-full text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                            style={{
                                backgroundColor: colors.primary
                            }}
                        >
                            {otpLoading ? 'Sending OTP...' : 'Send OTP'}
                        </button>
                    )}

                    {/* Register Button */}
                    {otpSent && (
                        <button
                            onClick={handleRegister}
                            disabled={!mobile || !otp || otp.length !== 6 || loading}
                            className="w-full text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                            style={{
                                backgroundColor: colors.primary
                            }}
                        >
                            {loading ? 'Registering...' : 'Register Vendor'}
                        </button>
                    )}

                    {/* Resend OTP */}
                    {showOtpField && (
                        <div className="text-center">
                            <button
                                onClick={handleRequestOTP}
                                disabled={otpLoading}
                                className="text-sm disabled:opacity-50 hover:opacity-80 transition-opacity"
                                style={{
                                    color: colors.primary
                                }}
                            >
                                Resend OTP
                            </button>
                        </div>
                    )}

                    {/* Back to Login */}
                    <button
                        onClick={handleBackToLogin}
                        disabled={loading || otpLoading}
                        className="w-full flex items-center justify-center gap-2 text-gray-600 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 hover:text-gray-800"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Login
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Register;
